import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Container, Row, Col, Button } from 'react-bootstrap';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import './BookAppointmentPage.css';
import toast from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Strict email regex: requires at least one @ and one . after @, no spaces
const emailStrictRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Prevent leading spaces and pure spaces input
const preventSpaces = (e) => {
  if (
    e.key === ' ' &&
    (!e.target.value || /^\s*$/.test(e.target.value + e.key))
  ) {
    e.preventDefault();
  }
};
const preventPasteSpaces = (e) => {
  if (/^\s*$/.test(e.clipboardData.getData('text'))) {
    e.preventDefault();
  }
};
// Allow only digits (0-9)
const allowOnlyDigits = (e) => {
  if (!/[0-9]/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete" && e.key !== "Tab") {
    e.preventDefault();
  }
};

// Prevent typing spaces
const blockSpaces = (e) => {
  if (e.key === " ") {
    e.preventDefault();
  }
};

// Prevent pasting spaces or invalid input
const blockInvalidPaste = (e, regex) => {
  const pasted = e.clipboardData.getData("text");
  if (!regex.test(pasted)) {
    e.preventDefault();
  }
};


// Reusable Yup string validator for fields that must not be empty or only spaces
const requiredTrimmedString = (msg) =>
  Yup.string()
    .required(msg)
    .test(
      'no-spaces-only',
      `${msg} cannot be empty or just spaces`,
      (value) => value && value.trim().length > 0 && !/^\s+$/.test(value),
    );

// Reusable input field component
const FormField = ({
  name,
  label,
  type = 'text',
  placeholder = '',
  as = 'input',
  disabled = false,
  onKeyDown = preventSpaces,
  onPaste = preventPasteSpaces,
  ...rest
}) => (
  <div className="mb-3">
    <label>{label}</label>
    <Field
      name={name}
      type={type}
      as={as}
      className={as === 'select' ? 'form-select' : 'form-control'}
      placeholder={placeholder}
      disabled={disabled}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      {...rest}
    />
    <ErrorMessage name={name} component="div" className="text-danger mt-1" />
  </div>
);

const BookAppointmentPage = () => {
  const [token, setToken] = useState('');
  const [hospital_id, setHospitalId] = useState('');
  const [subhospital_id, setSubhospitalId] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [hospitalDetails, setHospitalDetails] = useState({
    hospital_id: '',
    subhospital_id: '',
  });
  const [isFirstSectionSubmitted, setIsFirstSectionSubmitted] = useState(false);
  const [patientId, setPatientId] = useState(null);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [receptionistNoPrefix, setReceptionistNoPrefix] = useState('');
  const navigate = useNavigate();

  const generateRandomPassword = () =>
    Math.floor(10000 + Math.random() * 90000).toString();

  // Sync token and hospital info from localStorage
  useEffect(() => {
    const t = localStorage.getItem('token') || '';
    const userData = JSON.parse(localStorage.getItem('user')) || {};
    const hId = userData.hospital_id || '';
    const shId = userData.sub_hospital_id || '';
    setToken(t);
    setHospitalId(hId);
    setSubhospitalId(shId);
    setHospitalDetails({ hospital_id: hId, subhospital_id: shId });
  }, []);

useEffect(() => {
  if (hospital_id && subhospital_id && token) {
    const userData = JSON.parse(localStorage.getItem("user"));
    const receptionist_id =localStorage.getItem("receptionist_id");


    axios
      .get(`${BASE_URL}/api/patientAppointment/doctors`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          hospital_id, 
          subhospital_id,
          receptionist_id
        },
      })
      .then((res) => {
  const formattedDoctors = res.data.map((doc) => (
   {
    id: doc.id,
    name: doc.doctor_name,
    specialties: doc.specialties

  }));
  setDoctors(formattedDoctors);
})
      .catch((error) => console.error('Error fetching doctors:', error));

    axios
      .get(`${BASE_URL}/api/patientAppointment/receptionist-no-prefix`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { hospital_id, subhospital_id },
      })
      .then((response) => setReceptionistNoPrefix(response.data.prefix))
      .catch((error) =>
        console.error('Error fetching receptionist number prefix:', error),
      );
  }
}, [hospital_id, subhospital_id, token]);

  // Validation schemas
  const validationSchemaFirstSection = Yup.object({
    name: requiredTrimmedString('Name'),
    email: Yup.string()
      .required('Email is required')
      .matches(emailStrictRegex, 'Email must contain @ and . (e.g. user@mail.com)')
      .test(
        'no-spaces-only',
        'Email cannot be empty or just spaces',
        (value) => value && value.trim().length > 0 && !/^\s+$/.test(value),
      ),
    phone: requiredTrimmedString('Phone number').matches(
      /^\d{10}$/,
      'Phone number must be exactly 10 digits',
    ),
    personal_email: Yup.string()
      .nullable()
      .matches(emailStrictRegex, { message: 'Personal email must contain @ and .' })
      .test(
        'no-spaces-only',
        'Personal email cannot be only spaces',
        (value) => !value || (value.trim().length > 0 && !/^\s+$/.test(value)),
      ),
    extraPhone: Yup.string()
      .nullable()
      .matches(/^[0-9]{10}$/, 'Contact number must be exactly 10 digits')
      .test(
        'no-spaces-only',
        'Extra phone cannot be only spaces',
        (value) => !value || (value.trim().length > 0 && !/^\s+$/.test(value)),
      ),
    age: Yup.number().required('Age is required').positive().integer(),
    gender: Yup.string().required('Gender is required'),
  });

  const validationSchemaSecondSection = Yup.object({
    reason: requiredTrimmedString('Reason'),
    doctorId: Yup.string().required('Select a doctor'),
    appointmentDate: Yup.string().required('Select a date'),
    appointmentTime: Yup.string().required('Select a time'),
  });

  const handleFirstSectionSubmit = (values) => {
    if (!token) {
      toast.error('Authentication token is missing. Please log in again.');
      return;
    }
    const payload = {
      ...values,
      password: generateRandomPassword(),
      hospital_id,
      subhospital_id,
    };
    axios
      .post(`${BASE_URL}/api/patient/add-patient`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        toast.success(
          'Patient and user data saved successfully! Credentials sent to phone and emails.',
        );
        setPatientId(response.data.patient_id);
        setIsFirstSectionSubmitted(true);
      })
      .catch((error) => {
        console.error(
          'Error saving patient and user data:',
          error.response?.data || error.message,
        );
        // Display specific error message from backend
        const errorMessage = error.response?.data?.error || 'Error saving patient and user data';
        toast.error(errorMessage);
      });
  };

  const handleSecondSectionSubmit = async (values, { resetForm }) => {
    if (!token) {
      toast.error('Authentication token is missing. Please log in again.');
      return;
    }
    const [start_time, end_time] = values.appointmentTime
      .split(' -')
      .map((t) => t.trim());
    const payload = {
      patient_id: patientId,
      doctor_id: values.doctorId,
      appointment_date: values.appointmentDate,
      start_time: `${values.appointmentDate} ${start_time}:00`,
      end_time: `${values.appointmentDate} ${end_time}:00`,
      reason: values.reason,
      hospital_id,
      subhospital_id,
      status: 'Pending',
    };

    try {
      await axios.post(
        `${BASE_URL}/api/patientAppointment/create-appointment`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Appointment booked successfully!');
      resetForm();
      navigate('/receptionist_patient_appointment');
    } catch (error) {
      const msg =
        error.response?.data?.message || error.response?.data || error.message;
      toast.error('Error booking appointment: ' + msg);
      console.error(
        'Error booking appointment:',
        error.response?.data || error.message,
      );
    }
  };

  const handleDoctorChange = (doctorId, setFieldValue) => {
    setFieldValue('doctorId', doctorId);
    setFieldValue('appointmentDate', '');
    setFieldValue('appointmentTime', '');
    setAvailableTimes([]);
  };

  const handleDateChange = async (date, doctorId, setFieldValue) => {
    setFieldValue('appointmentDate', date);
    setFieldValue('appointmentTime', '');
    if (doctorId && date && token) {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/patientAppointment/doctor-availability`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              doctor_id: doctorId,
              date,
              hospital_id,
              subhospital_id,
            },
          },
        );
        setAvailableTimes(response.data);
      } catch (error) {
        console.error(
          'Error fetching available times:',
          error.response?.data || error.message,
        );
        toast.error('Error fetching available times');
      }
    }
  };

  return (
    <div className="d-flex">
      <Sidebar className="d-none d-md-block" />
      <Container className="book-appointment-page">
        <h3 className="global-heading mb-4">Book Appointment</h3>
        {!isFirstSectionSubmitted && (
          <Formik
            initialValues={{
              receipt_no: receptionistNoPrefix,
              name: '',
              email: '',
              phone: '',
              personal_email: '',
              extraPhone: '',
              age: '',
              gender: '',
              password: generateRandomPassword(),
              hospital_id: hospitalDetails.hospital_id,
              subhospital_id: hospitalDetails.subhospital_id,
            }}
            enableReinitialize={true}
            validationSchema={validationSchemaFirstSection}
            onSubmit={handleFirstSectionSubmit}
          >
            {() => (
              <Form>
                <Row>
                  <Col md={6}  >
                    <FormField
                      name="receipt_no"
                      label="Receipt No."
                      disabled
                      value={receptionistNoPrefix}
                    />
                  </Col>
                  <Col md={6}>
                    <FormField name="name" label="Name" placeholder="Enter name" />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormField
                      name="email"
                      label="Email"
                      type="email"
                      placeholder="Enter email"
                      onKeyDown={blockSpaces}
                      onPaste={(e) => blockInvalidPaste(e, /^[^\s@]+@[^\s@]+\.[^\s@]+$/)}
                    />
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label>Password</label>
                      <Field name="password">
                        {({ field }) => (
                          <input
                            {...field}
                            type="password"
                            className="form-control"
                            disabled
                          />
                        )}
                      </Field>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormField
                      name="phone"
                      label="Phone"
                      placeholder="Enter phone"
                       maxLength={10}
                      onKeyDown={allowOnlyDigits}
                      onPaste={(e) => blockInvalidPaste(e, /^[0-9]+$/)}
                    />
                  </Col>
                  <Col md={6}>
                    <FormField
                      name="personal_email"
                      label="Personal Email"
                      type="email"
                      placeholder="Enter personal email"
                      onKeyDown={blockSpaces}
                      onPaste={(e) => blockInvalidPaste(e, /^[^\s@]+@[^\s@]+\.[^\s@]+$/)}
                      
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormField
                      name="extraPhone"
                      label="Extra Phone"
                      placeholder="Enter extra phone (optional)"
                       maxLength={10}
                      onKeyDown={allowOnlyDigits}
                      onPaste={(e) => blockInvalidPaste(e, /^[0-9]+$/)}
                    />
                  </Col>
                  <Col md={6}>
                    <FormField
                      name="age"
                      label="Age"
                      type="number"
                      placeholder="Enter age"
                      onKeyDown={undefined}
                      onPaste={undefined}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormField
                      name="gender"
                      label="Gender"
                      as="select"
                      onKeyDown={undefined}
                      onPaste={undefined}
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </FormField>
                  </Col>
                </Row>
                <Button type="submit" className="btn btn-primary w-20 mt-3">
                  Add Patient
                </Button>
              </Form>
            )}
          </Formik>
        )}

        {isFirstSectionSubmitted && (
          <Formik
            initialValues={{
              reason: '',
              doctorId: '',
              appointmentDate: '',
              appointmentTime: '',
            }}
            validationSchema={validationSchemaSecondSection}
            onSubmit={handleSecondSectionSubmit}
          >
            {({ values, setFieldValue }) => (
              <Form>
                <Row>
                  <Col md={6}>
                    <FormField
                      name="doctorId"
                      label="Doctor"
                      as="select"
                      onKeyDown={undefined}
                      onPaste={undefined}
                      onChange={(e) => handleDoctorChange(e.target.value, setFieldValue)}
                    >
                      <option value="">Select Doctor</option>
                      {doctors.map((doc) => (
                        <option key={doc.id} value={doc.id}>
                          {doc.name} - {doc.specialties}
                        </option>
                      ))}
                    </FormField>
                  </Col>
                  <Col md={3}>
                    <FormField
                      name="appointmentDate"
                      label="Appointment Date"
                      type="date"
                      disabled={!values.doctorId}
                      onKeyDown={undefined}
                      onPaste={undefined}
                      onChange={(e) =>
                        handleDateChange(
                          e.target.value,
                          values.doctorId,
                          setFieldValue,
                        )
                      }
                    />
                  </Col>
                  <Col md={3}>
                    <FormField
                      name="appointmentTime"
                      label="Appointment Time"
                      as="select"
                      disabled={!values.appointmentDate}
                      onKeyDown={undefined}
                      onPaste={undefined}
                    >
                      <option value="">Select Time</option>
                      {availableTimes.map((time) => {
                        const [start, end] = time.split(' -');
                        const formatTime = (t) => {
                          const [h, m] = t.split(':');
                          const hour = parseInt(h, 10);
                          const suffix = hour >= 12 ? 'PM' : 'AM';
                          const hour12 = hour % 12 === 0 ? 12 : hour % 12;
                          return `${hour12}:${m} ${suffix}`;
                        };
                        const formattedTime = `${formatTime(start)} - ${formatTime(end)}`;
                        return (
                          <option key={time} value={time}>
                            {formattedTime}
                          </option>
                        );
                      })}
                    </FormField>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <FormField
                      name="reason"
                      label="Reason"
                      as="textarea"
                      placeholder="Enter reason"
                      rows="3"
                    />
                  </Col>
                </Row>
                <Button type="submit" className="btn btn-primary w-20 mt-3">
                  Book Appointment
                </Button>
              </Form>
            )}
          </Formik>
        )}
      </Container>
    </div>
  );
};

export default BookAppointmentPage;